import { Component, Input } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';

import { languageService } from '@unpispas/upp-base';
import { toastService } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { dataService } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { Ticket } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { DrawItem } from '@unpispas/upp-data';
import type { FloorArea } from '@unpispas/upp-data';

import type { UppSelectedEvent } from '@unpispas/feature/floor';

import { UppPinpadWorkComponent } from '../work/upp-work';

/**
 * Pinpad Mesas tab: table thumbs (and optional floor plan) driven by PlaceView.
 * Floor selection chrome lives on home/work (map icon + horizontal scroll).
 * Guest pay asks open table Attention (POS parity with topbar WAITER).
 */
@Component({
    selector: 'upp-pinpad-tables',
    templateUrl: './upp-tables.html',
    styleUrl: './upp-tables.scss',
})
export class UppPinpadTablesComponent {
    /** Host work shell (OpenTickets / TableFilter / FloorSelected). */
    @Input() work: UppPinpadWorkComponent | null = null;

    constructor(
        private lang: languageService,
        private data: dataService,
        private toast: toastService,
        private change: ChangeDetectorRef,
        public view: viewService,
    ) {}

    get place(): PlaceView | null {
        return this.data.alives.get(this.data.place) as PlaceView;
    }

    get tables(): Array<QrCodeView> {
        return this.place?.tables ?? [];
    }

    /** Selected floor from the work shell (header picker). */
    get FloorSelected(): FloorArea | null {
        return this.work?.FloorSelected ?? null;
    }

    get ShowFloor(): boolean {
        return this.FloorSelected !== null;
    }

    get HasTables(): boolean {
        return this.tables.length > 0;
    }

    tr(key: string): string {
        return this.lang.tr(key);
    }

    TableTrack(_index: number, table: QrCodeView): string | null {
        return table?._uuid || null;
    }

    /**
     * Attention → table warning panel (POS parity); else reuse with
     * `IsRecent && IsOpened` (1 → VIEW, ≥2 → Tickets+filter); else create + cart.
     */
    async OnSelectTable(table: QrCodeView | null): Promise<void> {
        if (!table || !this.work) {
            return;
        }

        if (table.Attention) {
            await this.work.OnPanel({
                main: 'view',
                scnd: 'table',
                args: { table },
            });
            this.change.markForCheck();
            return;
        }

        // POS table reuse: IsRecent && IsOpened (includes non-empty and empty drafts).
        const opened = (table.tickets ?? []).filter((t) => t.IsRecent && t.IsOpened);

        if (opened.length === 1) {
            const ticket = this.data.alives.get(opened[0].Copy()) as TicketView;
            await this.work.OnPanel({
                main: 'view',
                scnd: 'ticket',
                args: { ticket, mode: 'VIEW' },
            });
            this.change.markForCheck();
            return;
        }

        if (opened.length >= 2) {
            this.work.TableFilter = table;
            this.view.SetMainTab('VIEW', 'tickets');
            this.change.markForCheck();
            return;
        }

        await this.CreateTableTicket(table);
    }

    /**
     * Creates a new ticket for a free (or non-recent) table and opens the cart.
     * Parity with POS `CreateTableTicket` (DD-03-02).
     */
    async CreateTableTicket(table: QrCodeView | null): Promise<void> {
        if (!this.work || !this.place) {
            return;
        }

        if (!this.place.AllowTicketCommit) {
            void this.toast.ShowAlert(
                'warning',
                this.lang.tr('@warning_place_expired_title'),
            );
            return;
        }

        const created = new Ticket(null, this.data);
        created.created = new Date();
        created.updated = new Date();
        created.place = this.data.place;
        created.qrcode = table ? table.object : null;
        created.session = this.data.session;
        created.price = 0;
        created.refund = 0;
        created.taxes = 0;
        created.status = 'PR';

        const ticket = this.data.alives.get(created) as TicketView;
        await this.work.OnPanel({
            main: 'cart',
            scnd: null,
            args: {
                ticket,
                mode: 'EDIT',
            },
        });
        this.change.markForCheck();
    }

    OnDrawSelected(event: UppSelectedEvent): void {
        const draw = event?.draw as DrawItem | null;
        if (draw && ['BAR', 'TABLE'].includes(draw.type ?? '')) {
            const table = this.data.alives.get(draw.qrcode) as QrCodeView | null;
            void this.OnSelectTable(table);
        }
    }
}
